/**
 * What waits for a human, with a link to each place to decide it: suggestions from Claude
 * on runs, proposed cases and case versions, and judge prompt versions that are not
 * current. Shows nothing when the queue is empty.
 */
import { Inbox } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { EvalReviewQueue } from '../types/evalTypes';
import { formatDateTime, plural } from '../utils/evalFormat';

export const ReviewQueuePanel = ({ queue }: { queue: EvalReviewQueue }) => {
  const total = queue.runs.length + queue.cases.length + queue.judgePrompts.length;
  if (total === 0) return null;

  return (
    <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-violet-900">
        <Inbox size={16} /> Waiting for review
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        {queue.runs.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Suggestions on runs</h3>
            <ul className="space-y-0.5">
              {queue.runs.map((run) => (
                <li key={run.runId}>
                  <Link to={`/evals/${run.runId}`} className="text-blue-600 hover:underline">
                    {formatDateTime(run.startedAt)}
                  </Link>{' '}
                  <span className="text-gray-600">
                    {run.suiteId}, {plural(run.pendingSuggestions, 'suggestion')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {queue.cases.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Cases</h3>
            <ul className="space-y-0.5">
              {queue.cases.map((evalCase) => (
                <li key={`${evalCase.suiteId}~${evalCase.caseId}`}>
                  <Link to={`/evals/cases?suite=${evalCase.suiteId}&case=${evalCase.caseId}`} className="font-mono text-blue-600 hover:underline">
                    {evalCase.caseId}
                  </Link>{' '}
                  <span className="text-gray-600">
                    {evalCase.status === 'proposed' ? 'new case' : `${plural(evalCase.pendingVersions, 'new version')} (current v${evalCase.currentNumber})`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {queue.judgePrompts.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Judge prompts</h3>
            <ul className="space-y-0.5">
              {queue.judgePrompts.map((judge) => (
                <li key={`${judge.suiteId}~${judge.judgeName}`}>
                  <Link to={`/evals/judge-prompts?suite=${judge.suiteId}&judge=${judge.judgeName}`} className="text-blue-600 hover:underline">
                    {judge.suiteId} · {judge.judgeName}
                  </Link>{' '}
                  <span className="text-gray-600">
                    {plural(judge.pendingVersions, 'new version')} (current v{judge.currentNumber ?? '–'})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
